import type { MyAnnouncement } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Megaphone } from 'lucide-react';
import { api } from '../lib/api';
import { useMe } from '../lib/session';
import { Button } from '../ui/button';
import { toast } from '../ui/feedback';

const KEY = ['updates', 'announcements'];

/**
 * Объявления команды портала (ЛЦ-13) вверху страниц: важные — заметнее, «Скрыть» убирает у себя. В режиме
 * «глазами пользователя» объявления того человека видны, но скрыть их нельзя.
 */
export function AnnouncementsBanner() {
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: KEY,
    queryFn: () => api<MyAnnouncement[]>('/updates/announcements'),
    // Внешние участники объявлений не получают
    enabled: Boolean(me.tenant?.staff) && !me.user.demoRole,
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => api(`/updates/announcements/${id}/dismiss`, { method: 'POST' }),
    onSuccess: (_r, id) => {
      qc.setQueryData<MyAnnouncement[]>(KEY, (list) => (list ?? []).filter((a) => a.id !== id));
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось скрыть объявление', 'danger'),
  });
  const list = q.data ?? [];
  if (!list.length) return null;
  return (
    <div className="print:hidden">
      {list.map((a) => (
        <div
          key={a.id}
          role="status"
          className={clsx(
            'border-b text-ink',
            a.level === 'important' ? 'border-warning/40 bg-warning-soft' : 'border-info/30 bg-info-soft',
          )}
        >
          <div className="flex flex-wrap items-start gap-x-3 gap-y-1.5 px-4 py-2 md:px-8">
            <Megaphone
              className={clsx(
                'mt-0.5 size-5 shrink-0',
                a.level === 'important' ? 'text-warning' : 'text-info',
              )}
              aria-hidden
            />
            <div className="min-w-0 flex-1 basis-[220px] text-[13px] leading-5">
              <span className="font-semibold [overflow-wrap:anywhere]">{a.title}.</span>{' '}
              <span className="whitespace-pre-line text-ink-2 [overflow-wrap:anywhere]">{a.body}</span>
            </div>
            {me.viewAs ? null : (
              <Button
                size="sm"
                variant="ghost"
                loading={dismiss.isPending && dismiss.variables === a.id}
                onClick={() => dismiss.mutate(a.id)}
              >
                Скрыть
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
